import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { OPEN_ANNOUNCEMENT_EVENT } from "../Announcements";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronRight } from "lucide-react";
import { DateTime } from "luxon";
import { useNotifications } from "../../hooks/useNotifications";
import { sectionHeadingCls } from "../../styles/ui";
import PushToggle from "./PushToggle";

// Unread stays pinned on top whatever day it is from, but a worst-case pile
// (a busy user can get ~30 a day) must not push everything else off screen, so
// only the newest few show until "Show more" is clicked. Unread also EXPIRES
// with everything else after 7 days — the server deletes it, opened or not.
const UNREAD_PREVIEW = 15;

// Self-contained: owns its own open state and positions its panel off its
// own trigger, so it needs no wiring from Layout.jsx (unlike PMO Tools,
// which has to survive route navigation — a notification list doesn't;
// closing it on navigate-away is expected). The panel is viewport-`fixed`
// and positioned in a useLayoutEffect off the trigger's own
// getBoundingClientRect(), the same pattern Header.jsx's own NavGroup
// dropdown uses — the header is `h-14 overflow-hidden`, so an `absolute`
// panel here would get clipped.
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const navigate = useNavigate();

  const { unreadCount, unread, readToday, days, isLoading, markRead, markAllRead } =
    useNotifications({ open });
  const [showAllUnread, setShowAllUnread] = useState(false);
  // Which previous-day groups are expanded — collapsed by default, not persisted.
  const [openDays, setOpenDays] = useState(() => new Set());
  const toggleDay = (key) =>
    setOpenDays((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const el = triggerRef.current;
      if (!el) return;
      const M = 8;
      const r = el.getBoundingClientRect();
      const width = Math.min(384, window.innerWidth - 2 * M);
      const left = Math.max(M, Math.min(r.right - width, window.innerWidth - width - M));
      const top = r.bottom + 6;
      setPos({ top, left, width, maxHeight: Math.max(200, window.innerHeight - top - M) });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open]);

  const openNotification = (n) => {
    if (!n.read_at) markRead(n.id);
    setOpen(false);
    if (n.job_id) {
      navigate(
        n.visit_id ? `/jobs/${n.job_id}?visitId=${n.visit_id}` : `/jobs/${n.job_id}`
      );
    } else if (n.type === "announcement") {
      window.dispatchEvent(new CustomEvent(OPEN_ANNOUNCEMENT_EVENT, { detail: n.meta?.announcement_id }));
    } else if (typeof n.meta?.link === "string" && n.meta.link.startsWith("/")) {
      // Non-job notifications (e.g. annual reviews) carry their own in-app
      // path. Only a relative path — never navigate off the app.
      navigate(n.meta.link);
    }
  };

  const hasItems = unread.length > 0 || readToday.length > 0 || days.length > 0;
  const shownUnread = showAllUnread ? unread : unread.slice(0, UNREAD_PREVIEW);
  const hiddenUnread = unread.length - shownUnread.length;

  return (
    <div ref={ref} className="shrink-0 relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Notifications"
        title="Notifications"
        className="relative flex items-center justify-center w-8 h-8 rounded-lg text-ink-muted hover:text-ink hover:bg-surface-inset transition-colors"
      >
        <Bell className="w-4 h-4" aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-danger-600 ring-2 ring-surface text-xs font-semibold leading-none text-white"
            aria-hidden="true"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && pos && (
        <div
          role="menu"
          style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
          className="fixed z-panel bg-surface border border-line rounded-lg shadow-lg flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-line shrink-0">
            <span className="text-sm font-semibold text-ink">Notifications</span>
            <button
              type="button"
              onClick={() => markAllRead()}
              disabled={unreadCount === 0}
              className="text-xs font-medium text-brand-700 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Mark all as read
            </button>
          </div>
          <div className="overflow-y-auto">
            {isLoading && (
              <p className="px-3 py-6 text-sm text-ink-muted text-center">Loading…</p>
            )}
            {!isLoading && !hasItems && (
              <p className="px-3 py-6 text-sm text-ink-muted text-center">
                No notifications in the last 7 days.
              </p>
            )}
            {unread.length > 0 && (
              <div>
                <div className={`px-3 pt-2 pb-1 ${sectionHeadingCls}`}>Unread</div>
                {shownUnread.map((n) => (
                  <NotificationRow key={n.id} n={n} onOpen={openNotification} unread />
                ))}
                {hiddenUnread > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllUnread(true)}
                    className="block w-full px-3 py-2 text-xs font-medium text-brand-700 hover:text-brand-600 hover:bg-surface-inset text-left"
                  >
                    Show {hiddenUnread} more unread
                  </button>
                )}
              </div>
            )}
            {readToday.length > 0 && (
              <div>
                <div className={`px-3 pt-2 pb-1 ${sectionHeadingCls}`}>Read today</div>
                {readToday.map((n) => (
                  <NotificationRow key={n.id} n={n} onOpen={openNotification} />
                ))}
              </div>
            )}
            {days.length > 0 && (
              <div>
                <div className={`px-3 pt-2 pb-1 ${sectionHeadingCls}`}>Previous days</div>
                {days.map((d) => {
                  const expanded = openDays.has(d.key);
                  return (
                    <div key={d.key} className="border-b border-zinc-100 last:border-b-0">
                      <button
                        type="button"
                        onClick={() => toggleDay(d.key)}
                        aria-expanded={expanded}
                        className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-sm font-medium text-ink hover:bg-surface-inset"
                      >
                        <ChevronRight
                          className={`w-3.5 h-3.5 text-ink-muted transition-transform ${expanded ? "rotate-90" : ""}`}
                          aria-hidden="true"
                        />
                        <span>{d.label}</span>
                        <span className="text-xs font-normal text-ink-muted">({d.items.length})</span>
                      </button>
                      {expanded &&
                        d.items.map((n) => (
                          <NotificationRow key={n.id} n={n} onOpen={openNotification} />
                        ))}
                    </div>
                  );
                })}
              </div>
            )}
            <p className="px-3 py-2 text-xs text-ink-muted border-t border-line">
              Notifications are kept for 7 days.
            </p>
            <PushToggle />
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationRow({ n, onOpen, unread = false }) {
  const when = n.created_at ? DateTime.fromISO(n.created_at).toRelative() : "";
  return (
    <button
      type="button"
      onClick={() => onOpen(n)}
      className={`block w-full text-left px-3 py-2.5 border-b border-zinc-100 last:border-b-0 transition-colors hover:bg-surface-inset ${
        unread ? "bg-brand-50/60" : ""
      }`}
    >
      <div className="flex items-start gap-2">
        {unread && (
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-600 shrink-0" aria-hidden="true" />
        )}
        <div className={unread ? "" : "pl-3.5"}>
          <p className="text-sm text-ink font-medium leading-snug">{n.title}</p>
          {n.body && <p className="text-xs text-ink-muted mt-0.5 leading-snug">{n.body}</p>}
          <p className="text-xs text-ink-muted mt-1">{when}</p>
        </div>
      </div>
    </button>
  );
}
